import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { CirclePlus, Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { ScrollArea } from "components/ui/scroll-area";

import { AppLog } from "services";
import { StatusMsg } from "utils";
import { Service } from "utils/services";
import appStore from "store/appStore";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { getFFmpegInstance } from "utils/ffmpegInstance";
import { Rnd } from "react-rnd";
import { Button } from "components/ui/button";
import Loader2 from "components/app/loader/Loader2";
import { StudioMusicPlayer } from "./components/StudioMusicPlayer";
import { DialogMusic } from "./DialogMusic";

interface DialogProps {
  setIsDialogOpen: (value: boolean) => void;
  file: File;
  userPost: any;
  isOpen: boolean;
  isStudio: boolean;
  onCompleted: (url: string, thumbnails: string[], music: any) => void;
}

export function DialogTrimVideo({ setIsDialogOpen, file, userPost, isOpen, isStudio, onCompleted }: DialogProps) {
  const { t } = useTranslation();

  const { showDialogConfirm } = appStore();
  const [showLoader, setShowLoader] = useState(true);
  const [textLoader, setTextLoader] = useState("Initializing video...");

  const handleDiscardOriginalVideo = async () => {
    try {
      showDialogConfirm({
        title: "Discard",
        message: "Do you want to discard video ?",
        onSubmit: (_) => setIsDialogOpen(false),
      });
    } catch (err) {
      console.error("[error] Trimming failed:", err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent
        isShowBtnXClose={false}
        className="max-w-full lg:max-w-[700px]  h-[calc(100vh-80px)] py-2 px-0 flex flex-col "
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        {showLoader && <Loader2 text={textLoader} />}

        <DialogHeader>
          <DialogTitle className="mt-2 text-center ">{t("Trim Video")}</DialogTitle>

          <div
            className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
            onClick={handleDiscardOriginalVideo}
          >
            <X />
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 items-center  ">
          <VideoTrimmer file={file} userPost={userPost} isStudio={isStudio} onCompleted={onCompleted} setShowLoader={setShowLoader} setTextLoader={setTextLoader} />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

interface VideoTrimmerProps {
  file: File;
  userPost: any;
  isStudio: boolean;
  onCompleted: (url: string, thumbnails: string[], music: any) => void;
  setShowLoader: (value: boolean) => void;
  setTextLoader: (value: string) => void;
}

export default function VideoTrimmer({ file, userPost, isStudio, onCompleted, setShowLoader, setTextLoader }: VideoTrimmerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [duration, setDuration] = useState(0);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  let step: number = 0;

  const timelineWidth = 600;
  const thumbCount = 30;

  const maxTrimDuration = Service.toInteger(userPost?.video_length_in_second);

  // * ---- Music ---- * //
  const [music, setMusic] = useState<any>(null);
  const [musicUrl, setMusicUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isOpenDialogMusic, setIsOpenDialogMusic] = useState(false);

  const handleTrimVideo = async () => {
    if (loading || !videoUrl) return;

    const video = videoRef.current;
    if (!video) return;
    video.pause();

    setLoading(true);
    setShowLoader(true);
    setTextLoader("Trimming video ...");

    try {
      const ffmpeg: FFmpeg = await getFFmpegInstance();
      const inputVideo = "input.mp4";
      const trimmedVideo = "trimmed.mp4";
      const finalOutput = "output.mp4";
      const start = trimStart.toFixed(2);
      const durationTrim = (trimEnd - trimStart).toFixed(2);

      // Step 1: Write original video
      await ffmpeg.writeFile(inputVideo, await fetchFile(videoUrl));

      // Listen to FFmpeg progress
      ffmpeg.on("progress", ({ progress }) => {
        const percent = Math.round(progress * 100);
        setTextLoader(step === 0 ? `Trimming video ... ${percent}%` : `Merging music ...`);
        if (percent === 100) step++;
      });

      // Step 2: Trim the video
      await ffmpeg.exec(["-ss", start, "-i", inputVideo, "-t", durationTrim, "-c:v", "copy", "-c:a", "copy", trimmedVideo]);

      let finalFileName = trimmedVideo;

      // Step 3: If music exists, merge it
      if (musicUrl) {
        const inputAudio = "music.m4a";
        await ffmpeg.writeFile(inputAudio, await fetchFile(musicUrl));

        await ffmpeg.exec(["-i", trimmedVideo, "-i", inputAudio, "-c:v", "copy", "-c:a", "aac", "-map", "0:v:0", "-map", "1:a:0", "-shortest", finalOutput]);

        finalFileName = finalOutput;

        // Clean up music file
        await ffmpeg.deleteFile(inputAudio);
      }

      // Step 4: Read final output
      const data = await ffmpeg.readFile(finalFileName);
      const trimmedBlob = new Blob([data], { type: "video/mp4" });
      const objectUrl = URL.createObjectURL(trimmedBlob);
      const file = new File([trimmedBlob], "trimmed.mp4", { type: "video/mp4" });

      // Step 5: Cleanup all files
      await ffmpeg.deleteFile(inputVideo);
      await ffmpeg.deleteFile(trimmedVideo);
      if (musicUrl) {
        await ffmpeg.deleteFile(finalOutput);
      }

      // Step 6: Generate thumbnails and call completion
      const thumbs = await Service.getThumbnails(file, 5);
      if (thumbs) onCompleted(objectUrl, thumbs, music);
    } catch (err) {
      AppLog.show({
        message: "[Trimming failed]",
        error: err,
        status: StatusMsg.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const generateThumbnails = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    if (video.readyState < 1) {
      await new Promise((res) => {
        video.onloadedmetadata = res;
      });
    }

    const context = canvas.getContext("2d");
    const newThumbs: string[] = [];

    for (let i = 0; i < thumbCount; i++) {
      const time = (video.duration / thumbCount) * i;
      video.currentTime = time;

      await new Promise((res) => {
        video.onseeked = () => {
          context?.drawImage(video, 0, 0, canvas.width, canvas.height);
          newThumbs.push(canvas.toDataURL("image/jpeg"));
          res(true);
        };
      });
    }

    video.currentTime = 0;
    setThumbnails(newThumbs);
    setShowLoader(false);
    setTextLoader("");
  };

  const handleLoadedMetadata = () => {
    const dur = videoRef.current?.duration || 0;
    setDuration(dur);
    setTrimStart(0);
    setTrimEnd(maxTrimDuration > dur ? dur : maxTrimDuration);
    generateThumbnails();

    console.log("[handleLoadedMetadata] duration:", dur, "maxTrimDuration:", maxTrimDuration);
    console.log("[handleLoadedMetadata] final trimEnd:", maxTrimDuration > dur ? dur : maxTrimDuration);
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleTrimDrag = (x: number, width: number) => {
    const start = (x / timelineWidth) * duration;
    const end = ((x + width) / timelineWidth) * duration;

    const safeStart = Math.max(0, start);
    const safeEnd = Math.min(duration, end); // ⬅️ Cap it to avoid 1:40

    setTrimStart(safeStart);
    setTrimEnd(safeEnd);
  };

  const togglePlay = () => {
    try {
      if (!videoRef.current) return;
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    } catch (error) {
      AppLog.show({
        message: `[togglePlay]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
    }

    if (audioRef.current) {
      audioRef.current.muted = !muted;
    }

    setMuted(!muted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    const video = videoRef.current;
    const audio = audioRef.current;

    if (video) {
      video.currentTime = value;
      setCurrentTime(value);
    }

    // Sync audio if present
    if (audio && musicUrl) {
      // Set audio time to match video
      audio.currentTime = value;

      // If within audio duration, play audio; otherwise pause it
      if (value < audio.duration) {
        audio.play().catch(() => {});
      } else {
        audio.pause();
      }
    }
  };

  const onAddMusic = () => {
    setIsOpenDialogMusic(true);

    const video = videoRef.current;
    if (!video) return;
    video.pause();
  };

  const onRemoveMusic = () => {
    setMusic(null);
    setMusicUrl(null);

    const video = videoRef.current;
    if (!video) return;
    video.volume = 1;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () => {
      if (!isDragging) setCurrentTime(video.currentTime);
    };
    video.addEventListener("timeupdate", update);
    return () => video.removeEventListener("timeupdate", update);
  }, [isDragging]);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (!video || !audio || !musicUrl) return;

    const handlePlay = () => {
      audio.currentTime = video.currentTime;
      audio.play();
    };

    const handlePause = () => {
      audio.pause();
    };

    const handleSeeked = () => {
      audio.currentTime = video.currentTime;
    };

    const handleEnded = () => {
      audio.pause();
      audio.currentTime = 0;
      setPlaying(false);
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("ended", handleEnded);
    };
  }, [musicUrl]);

  const sliderPercentage = (currentTime / duration) * 100;

  return (
    <div className="px-4 space-y-4">
      <div className="w-full bg-muted/50 rounded-xl p-4 space-y-4 relative" ref={containerRef}>
        <div className="relative w-full max-w-3xl bg-black rounded-lg">
          <video
            ref={videoRef}
            src={videoUrl}
            onLoadedMetadata={handleLoadedMetadata}
            className="relative z-10 rounded w-full max-h-[350px]"
            muted={muted}
            playsInline
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
          />

          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center pointer-events-none z-20">
            <div className="flex items-end space-x-3">
              <Button variant="secondary" size="icon" className="pointer-events-auto w-8 h-8 rounded-full" onClick={togglePlay}>
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </Button>

              <Button variant="secondary" size="icon" className="pointer-events-auto w-8 h-8 rounded-full" onClick={toggleMute}>
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </Button>
            </div>
          </div>
        </div>

        <canvas ref={canvasRef} width={160} height={90} className="hidden"></canvas>

        {thumbnails && thumbnails.length > 0 && (
          <>
            <div className="relative w-full">
              <div className="mb-4 relative">
                <div
                  className="absolute z-20 top-1 transform -translate-x-1/2 text-xs text-white bg-red-600 rounded px-2 py-0.5 shadow select-none pointer-events-none"
                  style={{ left: `${sliderPercentage}%` }}
                >
                  {formatTime(currentTime)}
                </div>

                <div className="relative w-full h-6">
                  <div className="absolute top-1/2 transform -translate-y-1/2 left-0 right-0 flex justify-between items-center px-1">
                    {Array.from({ length: 17 }).map((_, i) => (
                      <div key={i} className="flex flex-col items-center w-[1px] justify-center">
                        {i === 0 ? (
                          <span className="text-xs mt-1">0.00</span>
                        ) : i === 16 ? (
                          <span className="text-xs mt-1">{formatTime(duration)}</span>
                        ) : (
                          <div className="w-px h-2 mt-1 bg-current" />
                        )}
                      </div>
                    ))}
                  </div>

                  <input
                    type="range"
                    min={0}
                    max={duration}
                    step={0.01}
                    value={currentTime}
                    onChange={handleSeek}
                    className="absolute top-0 left-0 w-full h-full bg-transparent appearance-none"
                    style={{ zIndex: 10 }}
                  />
                </div>
              </div>

              <div className="relative w-full mt-1 overflow-hidden border rounded border-gray-300">
                <div className="flex select-none pointer-events-none">
                  {thumbnails.map((src, idx) => (
                    <img key={idx} src={src} alt={`thumb-${idx}`} className="w-[20px] h-[60px] object-cover" />
                  ))}
                </div>

                <Rnd
                  default={{ x: 0, y: 0, width: 200, height: 60 }}
                  bounds="parent"
                  enableResizing={{
                    left: true,
                    right: true,
                    top: false,
                    bottom: false,
                  }}
                  dragAxis="x"
                  minWidth={(1 / duration) * timelineWidth}
                  maxWidth={(maxTrimDuration / duration) * timelineWidth}
                  onDragStart={() => {
                    console.log("onDragStart");
                    setIsDragging(true);
                  }}
                  onResize={() => {
                    console.log("onResize");
                  }}
                  onResizeStart={() => {
                    console.log("onResize");
                  }}
                  onDragStop={(_, d) => {
                    handleTrimDrag(d.x, d.node.offsetWidth);
                    setIsDragging(false);
                  }}
                  onResizeStop={(_, __, ref, ___, position) => {
                    handleTrimDrag(position.x, parseFloat(ref.style.width));
                    setIsDragging(false);
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    height: "60px",
                    borderRadius: "6px",
                    border: "2px solid #facc15",
                    backgroundColor: "rgba(250, 204, 21, 0.1)",
                  }}
                />
              </div>

              <div className="absolute bottom-0 w-[2px] h-[80px] bg-red-600 z-50" style={{ left: `${sliderPercentage}%` }} />
            </div>
            <p className="text-sm text-gray-600">
              Trim: {formatTime(trimStart)} → {formatTime(trimEnd)}
            </p>
            <Button onClick={handleTrimVideo} disabled={loading}>
              {loading ? "Trimming..." : "Trim Video"}
            </Button>
          </>
        )}

        {musicUrl && <audio ref={audioRef} src={musicUrl} className="hidden" />}
      </div>

      {isStudio && (
        <div className="w-full bg-muted/50 rounded-xl p-4 space-y-4 relative">
          {music ? (
            <StudioMusicPlayer music={music} onAddMusic={onAddMusic} onRemoveMusic={onRemoveMusic} />
          ) : (
            <Button className="gap-3" onClick={onAddMusic}>
              <CirclePlus /> Add Music
            </Button>
          )}
        </div>
      )}

      {isOpenDialogMusic && (
        <DialogMusic
          setIsDialogOpen={() => {
            setIsOpenDialogMusic(false);
          }}
          isOpen={isOpenDialogMusic}
          onSelectedMusic={(music) => {
            setIsOpenDialogMusic(false);

            setMusic(music);
            setMusicUrl(music.sound_url);

            const video = videoRef.current;
            if (!video) return;
            video.volume = 0;
          }}
        />
      )}
    </div>
  );
}

